/**
 * Enrolling one Synology - a DiskStation or a router - as an agentless managed device.
 *
 * The port of `SynologyEnrollForm` and its two places on the Network page: the inline panel on a
 * discovered row, and the "Add a Synology device by IP" card for a box no scan has reached. Both ask
 * for the same thing and post the same request; the manual one also needs the address typed in, and
 * a discovered one takes address, MAC and polling agent from the scan.
 *
 *   * TWO INDEPENDENT CHANNELS, and either alone is a working enrollment: SNMP for live health, an
 *     optional DSM/SRM admin login for the exact firmware, storage detail and pending updates. A blank
 *     login is an SNMP-only enrollment, not an incomplete form.
 *   * TEST BEFORE ENROLL, each channel on its own line - "SNMP fine, admin password wrong" is the
 *     answer that saves a support call. A probe stores nothing, and the device's own answer corrects
 *     the platform (DSM vs SRM) and fills an empty name from the model.
 *   * Self-signed certificates are accepted by default: every Synology ships with one, and leaving it
 *     off would make ticking HTTPS the obvious choice that silently does not work.
 *   * The password boxes are emptied once an enrollment succeeds - not after a Test, which would make
 *     someone retype both to act on the answer they just got.
 */
import { DiscoveredDeviceKind, SynologyPlatform, type DiscoveredDeviceDto, type SynologyCredentials } from '../../contracts.gen.js';
import * as crm from '../../api/crm.js';
import { describeError } from '../../api/http.js';
import { SnmpForm } from '../../ui/SnmpForm.js';
import { StatusLine } from '../../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkLine,
  comboBox,
  field,
  fieldRow,
  FONT,
  GAP,
  h2,
  muted,
  passwordBox,
  relayout,
  stackOf,
  text,
  textBox,
  vstack,
  wrappingCheck
} from '../../ui/layout.js';
import type { Card } from '../../ui/Card.js';
import type { PageContext } from '../page.js';

const PLATFORMS = ['DiskStation / RackStation (DSM)', 'Router (SRM)'] as const;

export interface SynologyEnrollOptions {
  ctx: PageContext;
  customerId: number;
  /** The scanned row this enrolls, or null for the add-by-IP card. */
  discovered: DiscoveredDeviceDto | null;
  /** Which on-site agent probes and polls: the one that found the row, else the page's online agent. */
  pollingDeviceId(): number | null;
  /** Called after a successful enrollment, with the sentence to show. */
  onEnrolled(message: string): void;
}

export interface SynologyEnrollPanel {
  readonly card: Card;
}

function whole(textValue: string): number | null {
  return /^\d+$/.test(textValue.trim()) ? Number(textValue.trim()) : null;
}

export function synologyEnrollPanel(options: SynologyEnrollOptions): SynologyEnrollPanel {
  const { ctx, customerId, discovered } = options;

  const nameBox = textBox({ text: discovered ? discovered.snmpSysName || discovered.hostname || '' : '' });
  const ipBox = textBox({ placeholder: '192.168.1.20' });
  const platform = comboBox<string>(PLATFORMS, {
    selectedIndex: discovered?.kind === DiscoveredDeviceKind.SynologyRouter ? 1 : 0
  });
  const pollBox = textBox({ text: '60' });
  const inventoryBox = textBox({ text: '15' });
  const snmp = new SnmpForm();
  snmp.setCommunity(discovered?.snmpCommunity);
  const apiUser = textBox();
  const apiPassword = passwordBox();
  const apiPort = textBox({ text: '0' });
  const https = wrappingCheck('HTTPS');
  const untrusted = wrappingCheck('Accept self-signed certificate', true);

  const snmpVerdict = muted('');
  const apiVerdict = muted('');
  snmpVerdict.visible = apiVerdict.visible = false;
  const status = new StatusLine();
  const testButton = button('Test connection', () => void test());
  const enrollButton = button('Enroll', () => void enroll(), true);

  const shell = card({ tint: discovered ? 'ok' : 'card', bordered: !discovered });
  const body = stackOf(
    vstack({ spacing: GAP.md }),
    h2(discovered ? 'Enroll this Synology device' : 'Add a Synology device by IP'),
    muted(
      "A Synology can't run the Windows agent, so an on-site agent polls it instead. SNMP gives live health, disks and volumes; a DSM/SRM admin account additionally gives the exact firmware, storage detail and pending updates. Either alone works. Credentials are stored encrypted."
    )
  );
  if (!discovered) body.controls.add(field('IP address', ipBox));
  body.controls.add(fieldRow(field('Name', nameBox), field('Type', platform)));
  body.controls.add(fieldRow(field('Poll every (seconds)', pollBox), field('Inventory every (minutes)', inventoryBox)));
  body.controls.add(text('SNMP', { font: FONT.bold }));
  body.controls.add(snmp.panel);
  body.controls.add(text('DSM / SRM admin account (optional)', { font: FONT.bold }));
  body.controls.add(
    muted(
      'Use a dedicated admin account without two-factor authentication - the CRM cannot answer a 2FA prompt. Leave blank to enroll over SNMP only.'
    )
  );
  body.controls.add(fieldRow(field('Username', apiUser), field('Password', apiPassword)));
  body.controls.add(field('Web port (0 = the platform default: DSM 5000/5001, SRM 8000/8001)', apiPort));
  body.controls.add(checkLine(https));
  body.controls.add(checkLine(untrusted));
  body.controls.add(stackOf(buttonRow(), testButton, enrollButton));
  body.controls.add(snmpVerdict);
  body.controls.add(apiVerdict);
  body.controls.add(status.label);
  shell.controls.add(body);

  function address(): string {
    return discovered ? discovered.ipAddress : ipBox.text.trim();
  }

  function selectedPlatform(): SynologyPlatform {
    return platform.selectedIndex === 1 ? SynologyPlatform.Srm : SynologyPlatform.Dsm;
  }

  function api(): SynologyCredentials | string {
    const port = whole(apiPort.text);
    if (port === null || port > 65535) return 'The web port must be a whole number (0 for the default).';
    const user = apiUser.text.trim();
    const password = apiPassword.text;
    if (!user || !password) return { username: '', password: '', port: 0, useHttps: false, allowUntrustedCertificate: false, hasLogin: false };
    return { username: user, password, port, useHttps: https.checked, allowUntrustedCertificate: untrusted.checked, hasLogin: true };
  }

  function setBusy(busy: boolean): void {
    testButton.enabled = enrollButton.enabled = !busy;
  }

  async function test(): Promise<void> {
    const poller = options.pollingDeviceId();
    if (poller === null) return status.error('No online agent on this customer to probe from.');
    if (!address()) return status.error("Enter the device's IP address.");
    const built = snmp.build();
    if (!built.ok) return status.error(built.error);
    const apiCreds = api();
    if (typeof apiCreds === 'string') return status.error(apiCreds);

    setBusy(true);
    status.busy('Probing…');
    try {
      const result = await crm.testSynology(customerId, {
        ipAddress: address(),
        platform: selectedPlatform(),
        snmp: built.credentials,
        api: apiCreds,
        pollingDeviceId: poller
      });
      if (ctx.cancelled()) return;
      snmpVerdict.text = (result.snmpOk ? 'SNMP OK — ' : 'SNMP failed — ') + result.snmpMessage;
      snmpVerdict.role = result.snmpOk ? 'muted' : 'danger';
      apiVerdict.text = (result.apiOk ? 'Web API OK — ' : 'Web API — ') + result.apiMessage;
      apiVerdict.role = result.apiOk || !apiCreds.hasLogin ? 'muted' : 'danger';
      snmpVerdict.visible = apiVerdict.visible = true;
      // The device knows better than the technician's guess which platform it is.
      if (result.detectedPlatform === SynologyPlatform.Srm) platform.selectedIndex = 1;
      else if (result.detectedPlatform === SynologyPlatform.Dsm) platform.selectedIndex = 0;
      if (!nameBox.text.trim() && result.model?.trim()) nameBox.text = result.model;
      if (result.anyOk) status.ok('Reachable - you can enroll it.');
      else status.error('Neither channel answered; nothing to enroll yet.');
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      setBusy(false);
      relayout(shell);
    }
  }

  async function enroll(): Promise<void> {
    const poller = options.pollingDeviceId();
    if (!discovered && !address()) return status.error("Enter the device's IP address.");
    if (!discovered && poller === null) return status.error('No online agent on this customer to poll from.');
    const poll = whole(pollBox.text);
    const inventory = whole(inventoryBox.text);
    if (poll === null || poll <= 0) return status.error('Poll every must be a whole number of seconds.');
    if (inventory === null || inventory <= 0) return status.error('Inventory every must be a whole number of minutes.');
    const built = snmp.build();
    if (!built.ok) return status.error(built.error);
    const apiCreds = api();
    if (typeof apiCreds === 'string') return status.error(apiCreds);

    setBusy(true);
    status.busy('Enrolling…');
    try {
      await crm.enrollSynology(customerId, {
        displayName: nameBox.text.trim(),
        platform: selectedPlatform(),
        pollSeconds: poll,
        inventorySeconds: inventory * 60,
        snmp: built.credentials,
        api: apiCreds,
        discoveredDeviceId: discovered?.id ?? null,
        ipAddress: address(),
        pollingDeviceId: poller
      });
      if (ctx.cancelled()) return;
      snmp.clearPasswords();
      apiPassword.text = '';
      options.onEnrolled(`Enrolled ${nameBox.text.trim() || address()} - it appears under Monitored devices.`);
    } catch (error) {
      if (!ctx.cancelled()) status.error(describeError(error));
    } finally {
      setBusy(false);
      relayout(shell);
    }
  }

  return { card: shell };
}
